import { Cell, DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';
import { defaultMemoize } from 'reselect/lib';
import { SiteMembershipGridColumns } from '../../app/content/SiteMembershipGridColumns';
import { Messages } from '../../codegen/Localize';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ReactCheckboxTristate } from '../../common/react/components/ReactCheckboxTristate';
import { SiteMembershipFilters } from '../../common/react/components/SiteMembershipFilters';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { ListResult } from '../../ts/fetchers/ListResult';
import { Filter } from '../../ts/helpers/Filter';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { ISliceable } from '../../ts/models/ISliceable';
import { SiteNameWithIds } from '../../ts/resources/SiteNameWithIds';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { ISiteNameWithId } from '../../ts/ServerApi/types/ISiteNameWithId';
import { ISiteRole, SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IUserRole } from '../../ts/ServerApi/types/IUserRole';
import { ISiteRoleChange, IUsersWithSiteRolesModel } from '../services/SiteRolesPickerClientService';
import { SliceableFactory } from '../../ts/services/SliceableFactory';
import { UserRoleSelections } from './EditSiteMembershipDialog';
import { SiteRolesPickerAdminCheckboxHelper } from './helpers/SiteRolesPickerAdminCheckboxHelper';
import { SiteRolesPickerDataGridHelper } from './helpers/SiteRolesPickerDataGridHelper';
import { SiteRolesPickerFiltersHelper } from './helpers/SiteRolesPickerFiltersHelper';
import { SiteRolesPickerContainerEmptyTemplate } from './SiteRolesPickerContainerEmptyTemplate';

export interface SiteRolesPickerContainerState {
    // TODO: 801446, siteNames and siteNamesWithFilter to be collapsed into one
    siteNames: ISiteNameWithId[];
    siteNamesWithFilter: ISiteNameWithId[];
    siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary;
    siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary;
    availableSiteFilters: string[];
    currentSiteFilter: string;
    siteSearchQuery: string;
    adminCheckboxState: CheckboxState;

    //TODO: 801955, take these backups out of the state and put as class member
    siteRowCheckboxPropMapBackup: SiteRoleCheckboxPropsDictionary;
    siteRowDropdownPropMapBackup: SiteRoleSelectorPropsDictionary;
    lastSiteFilter: string;
}

export interface SiteRolesPickerContainerProps {
    changes: (siteRoles: ISiteRoleChanges) => void;
    busy: boolean;
    angularContext: AngularContext;
    userRoleSelections?: UserRoleSelections;
}

export interface ISiteRoleChanges {
    siteRoles: _.Dictionary<ISiteRoleChange>; // changes keyed by siteId
    serverAdmin: string; // CheckboxState
}

export interface SiteRoleSelectorPropsDictionary {
    [siteId: string]: SiteRolePickerMenu.Props;
}

export interface SiteRoleCheckboxPropsDictionary {
    [siteId: string]: ReactCheckboxTristate.Props;
}

export let SiteMembershipDialogFilters = {
    AllSites: 'siteMembershipAllSites',
    AssignedSites: 'siteMembershipAssignedSites',
    SearchResults: 'siteMembershipSearchResults'
};

export interface IUserCountsAndRolesBySiteId extends _.Dictionary<{
    count: number;
    siteRole: string;
  }> { }

const DefaultDataGridConfig: DataGrid.Config = {
    minimumColumnWidth: 38, // this is the smallest size we want the selection column to be
    maxInitialColumnWidth: -1 // negative number removes maximum width so takes up column width specified
};

export class SiteRolesPickerContainer extends React.Component<SiteRolesPickerContainerProps, SiteRolesPickerContainerState> {

    private DefaultCheckboxProp: ReactCheckboxTristate.Props = {
        checkboxState: CheckboxState.empty,
        clicked: () => {},
        disabled: false
    };

    private createDefaultDropdownProp(site: ISiteNameWithId): SiteRolePickerMenu.Props {
        return {
            translate: (key: string) => this.translate.instant(key),
            siteRoleOptions: this.siteRolesResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator),
            getSiteRoleOptionBySiteRole: (siteRole: string) => this.siteRolesResource.getSiteRoleOptionBySiteRole(siteRole),
            onOptionSelected: (siteRoleOption: SiteRoleOption) => this.onSiteRoleSelected(site, siteRoleOption),
            selectedRole: this.siteRolesResource.getSiteRoleOptionBySiteRole(DefaultSiteRole),
            isDisabled: false
        };
    }

    private rowStoreWrapper = defaultMemoize((siteNamesWithFilter: ISiteNameWithId[]): DataGrid.RowStore => AngularToReactDataGridBridge.getReactDataGridRowStore(this.sliceableFactory.fromArray(siteNamesWithFilter)));

    private getInitialStateFromSiteNames(siteNames: ISiteNameWithId[]): SiteRolesPickerContainerState{
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {};
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {};
        for (let site of siteNames) {
            siteRowDropdownPropMap[site.id] = this.createDefaultDropdownProp(site);
            siteRowCheckboxPropMap[site.id] = this.DefaultCheckboxProp;
        }
        return {...this.state, siteRowDropdownPropMap, siteRowCheckboxPropMap};
    }

    private siteNamesResource: SiteNameWithIds = this.props.angularContext.getService('SiteNames');
    private siteRolesResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
    private sliceableFactory: SliceableFactory = this.props.angularContext.getService('SliceableFactory');
    private translate = this.props.angularContext.getService('$translate');

    private columns = SiteMembershipGridColumns.columns(this.getPropsForDropdownCell.bind(this), this.getPropsForCheckboxCell.bind(this), this.props.angularContext);
    private columnWrapper = defaultMemoize((siteToCheckboxPropMap: SiteRoleCheckboxPropsDictionary, siteToDropdownPropMap: SiteRoleSelectorPropsDictionary) => {return this.newCols();});
    private unmounted: boolean = false;

    private newCols() {
        return [{...this.columns[0]}, {...this.columns[1]}, {...this.columns[2]}];
    }

    public constructor(props: SiteRolesPickerContainerProps) {
        super(props);
        this.state = {
            siteNames: [],
            siteNamesWithFilter: [],
            lastSiteFilter: SiteMembershipDialogFilters.AllSites,
            siteRowCheckboxPropMapBackup: null,
            siteRowDropdownPropMapBackup: null,
            availableSiteFilters: [SiteMembershipDialogFilters.AllSites,SiteMembershipDialogFilters.AssignedSites],
            currentSiteFilter: SiteMembershipDialogFilters.AllSites,
            siteSearchQuery: '',
            adminCheckboxState: CheckboxState.empty,
            siteRowCheckboxPropMap: {},
            siteRowDropdownPropMap: {}
        };
        SliceableHelper.fetchAll(this.siteNamesResource.all()).then((siteNames: ListResult<ISiteNameWithId>) => {
            if (!this.unmounted) {
                let initialState = this.getInitialStateFromSiteNames(siteNames.items);
                this.setState({...initialState, siteNamesWithFilter: siteNames.items, siteNames: siteNames.items});
            }
        });
    }

    public componentWillReceiveProps(nextProps: SiteRolesPickerContainerProps) {
        let state = this.state;
        if (nextProps.busy !== this.props.busy) {
            state = {...state, ...SiteRolesPickerDataGridHelper.getStateOnBusyChange(this.state, nextProps.busy)};
        }
        if (nextProps.userRoleSelections !== this.props.userRoleSelections) {
            state = {...state, ...SiteRolesPickerDataGridHelper.getStateOnModifyRoles(this.state, nextProps.userRoleSelections, this.siteRolesResource)};
        }
        if (state !== this.state) {
            if (!this.unmounted) {
                this.setState(state);
            }
        }
    }

    public componentWillUnmount() {
        this.unmounted = true;
    }

    private readonly selectAllSiteFilter = () => {
        this.filterSelectedHandler(SiteMembershipDialogFilters.AllSites);
    }

    private readonly filterSelectedHandlerCallback = (value: string) => {
        this.filterSelectedHandler(value);
    }

    private readonly onSearchInputTextChangeCallback = (searchInput: string) => {
        this.onSearchInputTextChange(searchInput);
    }

    private readonly onCancelSearchCallback = () => {
        this.onCancelSearch();
    }

    private readonly onCellClickCallback = (row: any, columnIndex: string, cell: Cell, e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        this.onCellClicked(row, columnIndex);
    }

    private readonly adminCheckboxClickedCallback = () => {
        this.adminCheckboxClicked();
    }

    public render() {
    return (
        <div className="tb-fill tb-site-roles-picker" tb-test-id="picker-control">
            <SiteMembershipFilters filters={this.state.availableSiteFilters}
                                onFilterSelected={this.filterSelectedHandlerCallback}
                                siteFilterSelection={this.state.currentSiteFilter}
                                onCancelSearch={this.onCancelSearchCallback}
                                onSearchInputTextChange={this.onSearchInputTextChangeCallback}
                                searchInputText={this.state.siteSearchQuery}
                                translate={this.translate.instant}/>
            <div className={'tb-bottom tb-grid-container'}>
                <DataGrid emptyChild={<SiteRolesPickerContainerEmptyTemplate actionHandler = {this.selectAllSiteFilter}
                                                                            title = { this.getEmptyTemplateModelTitle() }
                                                                            actionTitle = {this.translate.instant('assignSites')}
                                                                            siteNames = {this.state.siteNames} />}
                    hackyUpdate={0}
                    cols={this.columnWrapper(this.state.siteRowCheckboxPropMap, this.state.siteRowDropdownPropMap)}
                    config={DefaultDataGridConfig}
                    rowStore={this.rowStoreWrapper(this.state.siteNamesWithFilter)}
                    showHorizontalGridLines={false}
                    onCellClick={this.onCellClickCallback}/>
            </div>
            <div className={'tb-admin-checkbox'}>
                <ReactCheckboxTristate checkboxState={this.state.adminCheckboxState}
                                        clicked={this.adminCheckboxClickedCallback}
                                        disabled={this.props.busy}
                                        label={Messages.serverAdminSelect()} />
            </div>
        </div>
      );
    }

    private getPropsForCheckboxCell(site: ISiteNameWithId): ReactCheckboxTristate.Props {
        if (!this.state.siteRowCheckboxPropMap[site.id]) {
            return this.DefaultCheckboxProp;
        }
        return this.state.siteRowCheckboxPropMap[site.id];
    }


    private getPropsForDropdownCell(site: ISiteNameWithId): SiteRolePickerMenu.Props {
        if (!this.state.siteRowDropdownPropMap[site.id]) {
            return this.createDefaultDropdownProp(site);
        }
        return this.state.siteRowDropdownPropMap[site.id];
    }

    private onSiteRoleSelected(site: ISiteNameWithId, siteRoleOption: SiteRoleOption): void {
        let newState = SiteRolesPickerDataGridHelper.getStateOnSiteRoleSelected(this.state, site, siteRoleOption, this.siteRolesResource);
        this.props.changes(this.getSiteRoleChanges(newState));
        if (!this.unmounted) {
            this.setState(newState);
        }
    }

    private onCellClicked(row: any, columnIndex?: string) {
        //if the site role select menu is visible, clicking on the column should not toggle the row.
        if ((columnIndex !== '2' || this.state.siteRowCheckboxPropMap[row.id].checkboxState !== CheckboxState.checked) &&
            this.state.adminCheckboxState !== CheckboxState.checked && !this.props.busy && this.state.adminCheckboxState !==CheckboxState.mixed) {
            let newState: SiteRolesPickerContainerState = SiteRolesPickerDataGridHelper.getStateOnCellClicked(this.state, row, this.siteRolesResource);
            this.props.changes(this.getSiteRoleChanges(newState));
            if (!this.unmounted) {
                this.setState(newState);
            }
        }
    }

    private adminCheckboxClicked() {
        let adminCheckboxState = CheckboxState.toggle(this.state.adminCheckboxState as string);
        let newState: SiteRolesPickerContainerState;

        if (adminCheckboxState === CheckboxState.checked) {
            newState = SiteRolesPickerAdminCheckboxHelper.getStateWhenChecked(this.state, this.siteRolesResource, this.props.userRoleSelections);
            this.props.changes(this.getSiteRoleChanges(newState));
            this.setStateThenFilter(newState, this.state.currentSiteFilter);
        } else {
            newState = SiteRolesPickerAdminCheckboxHelper.getStateWhenEmpty(this.state, this.props.userRoleSelections, this.siteRolesResource);
            this.props.changes(this.getSiteRoleChanges(newState));
            this.setStateThenFilter(newState, this.state.currentSiteFilter);
        }
    }

    public getSiteRoleChanges(newState: SiteRolesPickerContainerState): ISiteRoleChanges {
        let siteRolesPickerChanges: ISiteRoleChanges =
        {
            siteRoles: {},
            serverAdmin: undefined
        };

        if (newState.adminCheckboxState === CheckboxState.checked) {
            siteRolesPickerChanges.serverAdmin = CheckboxState.checked;
        } else {
            for (let site in newState.siteRowCheckboxPropMap) {
                let siteRole = newState.siteRowDropdownPropMap[site].selectedRole;
                let checkboxState = newState.siteRowCheckboxPropMap[site].checkboxState as string;
                let originalCheckboxState = this.getOriginalCheckboxStateForSite(this.props.userRoleSelections, site);
                if (checkboxState === CheckboxState.checked || originalCheckboxState !== checkboxState) {
                    siteRolesPickerChanges.siteRoles[site] = {
                        checkboxState: checkboxState,
                        siteRole: siteRole.role,
                        siteName: this.state.siteNames.find(siteNameWithId => siteNameWithId.id === site).name
                    };
                }
            }
            if (this.props.userRoleSelections && this.props.userRoleSelections.numSelectedServerAdmins > 0) {
                siteRolesPickerChanges.serverAdmin = newState.adminCheckboxState as string;
            }
        }
        return siteRolesPickerChanges;
    }

    // input a site ID, returns the initial checkbox state for the given site (checked, empty, or mixed)
    public getOriginalCheckboxStateForSite(userRoleSelections: UserRoleSelections, siteId: string): string {
        if (!userRoleSelections || !userRoleSelections.userCountsAndRolesBySiteId[siteId]) {
            return CheckboxState.empty;
        } else if (userRoleSelections.userCountsAndRolesBySiteId[siteId].count === userRoleSelections.numSelectedUsers) {
            return CheckboxState.checked;
        } else {
            return CheckboxState.mixed;
        }
    }

    private getEmptyTemplateModelTitle(): string {
        if (this.state.currentSiteFilter === SiteMembershipDialogFilters.SearchResults) {
            return this.translate.instant('noMatchingResults');
        }
        return this.translate.instant('noAssignedSites');
    }

    private onSearchInputTextChange(searchInput: string) {
        this.setStateThenFilter(SiteRolesPickerFiltersHelper.getStateOnTextChange(this.state, searchInput), SiteMembershipDialogFilters.SearchResults);
    }

    private onCancelSearch() {
        if (!this.unmounted) {
            this.setState(SiteRolesPickerFiltersHelper.getStateOnCancelSearch(this.state), () => {
                if (!this.unmounted) {
                    this.filterSelectedHandler(this.state.currentSiteFilter);
                }
            });
        }
    }

    private filterSelectedHandler(value: string) {
        switch(value) {
            case SiteMembershipDialogFilters.AllSites:
                if (!this.unmounted) {
                    this.setState(SiteRolesPickerFiltersHelper.getStateForAllSitesFilter(this.state));
                }
                return;
            case SiteMembershipDialogFilters.AssignedSites:
                if (!this.unmounted) {
                    this.setState(SiteRolesPickerFiltersHelper.getStateForAssignedSitesFilter(this.state));
                }
                return;
            case SiteMembershipDialogFilters.SearchResults:
                SliceableHelper.fetchAll(this.siteNamesResource.all().withFilters([Filter.search(this.state.siteSearchQuery)])).then((searchResults: ListResult<ISiteNameWithId>) => {
                    if (!this.unmounted) {
                        this.setState(SiteRolesPickerFiltersHelper.getStateForSearchResultsFilter(this.state, searchResults.items));
                    }
                });
                return;
            }
    }

    private setStateThenFilter(state: SiteRolesPickerContainerState, value: string) {
        if (!this.unmounted) {
            this.setState(state, () => {
                if (!this.unmounted) {
                    this.filterSelectedHandler(value);
                }
            });
        }
    }
}
